import React, { useContext, useState } from "react";
import { partition, startCase, toLower } from "lodash";
import { OmniboxSearchResult } from "../../generated/graphql";
import { spacing } from "../../stitches.config";
import { FlexRow } from "../Common/__styles__/Layout";
import { EmptyState } from "../Common/EmptyState";
import { IconNames } from "../Common/Icons/LucideIcons";
import { Tag } from "../Common/Tag";
import { Body } from "../Common/Typography";
import { SelectionCard } from "../Inputs/SelectionCard";
import { SearchResultProps } from ".";
import { SearchContext } from "./SearchContext";

const formatSearchResultAddress = (address?: Maybe<string>) => {
  return startCase(toLower(address ?? ""));
};

const displayForResult = (
  result: OmniboxSearchResult,
): {
  title?: Maybe<string>;
  description?: Maybe<string>;
  iconName: IconNames;
  type: string;
  value: SearchResultProps;
} => {
  if (result.__typename === "ParcelSearchResult") {
    return {
      title: result.parcelId,
      description: formatSearchResultAddress(result.address),
      iconName: "land-plot",
      type: "Parcel",
      value: {
        point: {
          latitude: result.interiorPoint.coordinates[1],
          longitude: result.interiorPoint.coordinates[0],
        },
        address: result.address ?? "",
        geocacheParcelId: result.id,
        city: result.city,
        state: result.state,
        zip: result.zip,
      },
    };
  } else if (result.__typename === "PropertySearchResult") {
    return {
      title: formatSearchResultAddress(result.streetAddress),
      iconName: "home",
      type: "Property",
      value: {
        point: {
          latitude: result.geometry.coordinates[1],
          longitude: result.geometry.coordinates[0],
        },
        address: result.streetAddress ?? "",
        propertyId: result.id,
        geocacheParcelId: result.geocacheParcelId,
        streetAddress: result.streetAddress,
        city: result.city,
        state: result.state,
        zip: result.zip,
      },
    };
  } else {
    throw new Error(`Unknown result type: ${result.__typename}`);
  }
};

const OmniboxResultItem = ({ result }: { result: OmniboxSearchResult }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { title, description, iconName, type, value } =
    displayForResult(result);

  return (
    <SelectionCard.Item
      size="compact"
      borderStyle="ghost"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      value={value}
    >
      <FlexRow style={{ gap: spacing.m.value }}>
        <SelectionCard.CommonContent
          iconName={iconName}
          title={title}
          description={description}
        />
        <div style={{ opacity: isHovered ? 1 : 0 }}>
          {/* TODO: check on tag height */}
          <Tag styleVariant="default">{type}</Tag>
        </div>
      </FlexRow>
    </SelectionCard.Item>
  );
};

export const OmniboxResults = ({
  handleSearchResultClick,
}: {
  handleSearchResultClick: (searchResult: SearchResultProps) => void;
}) => {
  const { results, query, loadingSearch } = useContext(SearchContext);

  if (loadingSearch) {
    return <div>Loading...</div>;
  }

  if (results.length === 0 && !loadingSearch && query) {
    return (
      <EmptyState
        icon="missing-location"
        paddingTop={spacing["2xl"].value}
        title="No results found"
        body="Check the spelling or search by street, city, or ZIP, then use the map to explore nearby."
      />
    );
  }

  const [strictMatches, fuzzyMatches] = partition(
    results,
    result => result.strictMatch,
  );

  return (
    <SelectionCard.Group
      direction="vertical"
      spacing="compact"
      handleSelection={handleSearchResultClick}
    >
      {strictMatches.map(result => (
        <OmniboxResultItem result={result} key={result.id} />
      ))}
      {fuzzyMatches.length > 0 && (
        <>
          <Body
            type="emphasis"
            size="small"
            style={{
              paddingTop: strictMatches.length > 0 ? spacing.m.value : 0,
            }}
          >
            Related Results
          </Body>
          {fuzzyMatches.map(result => (
            <OmniboxResultItem result={result} key={result.id} />
          ))}
        </>
      )}
    </SelectionCard.Group>
  );
};
